export const ONTOLOGY_BUILD_EVENT_CONTRACT_VERSION =
  "ontology_build_event.v1" as const;

export type OntologyBuildEventType =
  | "run_started"
  | "stage_started"
  | "schema_inspected"
  | "group_created"
  | "candidate_proposed"
  | "candidate_validated"
  | "conflict_detected"
  | "stage_retrying"
  | "stage_recovered"
  | "stage_completed"
  | "run_completed"
  | "run_failed"
  | "run_superseded";

export type OntologyBuildStage =
  | "inventory_normalize"
  | "group"
  | "infer_entities"
  | "bind_sources"
  | "resolve_identity"
  | "infer_relationships_metrics"
  | "merge_reconcile"
  | "validate"
  | "persist_suggestions"
  | "completed";

export type OntologyBuildEventStatus =
  | "queued"
  | "running"
  | "proposed"
  | "validated"
  | "retrying"
  | "completed"
  | "failed";

export type OntologyBuildArtifactType =
  | "domain"
  | "entity"
  | "source_binding"
  | "identity_mapping"
  | "business_meaning"
  | "relationship"
  | "metric"
  | "conflict";

export interface OntologyBuildEvidenceRef {
  type:
    | "asset"
    | "column"
    | "constraint"
    | "lineage"
    | "profile_summary"
    | "catalog";
  ref: string;
  label?: string | null;
}

export interface OntologyBuildEventPayload {
  contract_version: typeof ONTOLOGY_BUILD_EVENT_CONTRACT_VERSION;
  group_id?: string | null;
  group_key?: string | null;
  artifact_type?: OntologyBuildArtifactType | null;
  artifact_ref?: string | null;
  artifact_label?: string | null;
  source_artifact_ref?: string | null;
  target_artifact_ref?: string | null;
  confidence_score?: number | null;
  evidence_refs?: OntologyBuildEvidenceRef[];
  counts?: Record<string, number>;
  retry_attempt?: number | null;
  retry_limit?: number | null;
  checkpoint_ref?: string | null;
  error_code?: string | null;
  iteration?: number | null;
  stabilized?: boolean | null;
  recovered?: boolean | null;
}

export interface OntologyGenerationRunEvent {
  contract_version?: typeof ONTOLOGY_BUILD_EVENT_CONTRACT_VERSION;
  id: number;
  event_id?: number | string | null;
  workspace_id: number;
  run_id: number;
  event_type: OntologyBuildEventType;
  stage_name: OntologyBuildStage;
  status: OntologyBuildEventStatus;
  message: string;
  progress_percentage?: number | null;
  payload: OntologyBuildEventPayload;
  created_at: string;
  timestamp?: string;
}

export type OntologyGenerationConnectionState =
  | "idle"
  | "connecting"
  | "live"
  | "polling"
  | "recovered"
  | "completed"
  | "failed"
  | "permission_denied";

export interface OntologyBuildProposal {
  artifactRef: string;
  artifactType: OntologyBuildArtifactType;
  label: string;
  sourceArtifactRef?: string;
  targetArtifactRef?: string;
  confidenceScore?: number;
  sourceBindingCount?: number;
  status: "proposed" | "validated";
  eventId: number;
}

export interface OntologyBuildProjection {
  events: OntologyGenerationRunEvent[];
  latestEvent?: OntologyGenerationRunEvent;
  stage: OntologyBuildStage;
  progress: number;
  counts: Record<string, number>;
  proposals: OntologyBuildProposal[];
  feed: OntologyGenerationRunEvent[];
  isRetrying: boolean;
  hasRecovered: boolean;
  isCompleted: boolean;
  isFailed: boolean;
}

const INITIAL_PROJECTION: OntologyBuildProjection = {
  events: [],
  stage: "inventory_normalize",
  progress: 0,
  counts: {},
  proposals: [],
  feed: [],
  isRetrying: false,
  hasRecovered: false,
  isCompleted: false,
  isFailed: false,
};

export function projectOntologyBuildEvents(
  incomingEvents: OntologyGenerationRunEvent[],
): OntologyBuildProjection {
  const events = deduplicateEvents(incomingEvents);
  if (events.length === 0) return INITIAL_PROJECTION;

  const counts: Record<string, number> = {};
  const proposalsByRef = new Map<string, OntologyBuildProposal>();
  let latestProgress = 0;
  let hasRecovered = false;

  events.forEach((event) => {
    Object.entries(event.payload?.counts ?? {}).forEach(([key, value]) => {
      if (typeof value === "number" && Number.isFinite(value))
        counts[key] = value;
    });
    if (typeof event.progress_percentage === "number") {
      latestProgress = clamp(event.progress_percentage, 0, 100);
    }
    if (event.event_type === "stage_recovered") hasRecovered = true;
    if (
      (event.event_type === "candidate_proposed" ||
        event.event_type === "candidate_validated") &&
      event.payload?.artifact_ref &&
      event.payload?.artifact_type &&
      event.payload?.artifact_label
    ) {
      proposalsByRef.set(event.payload.artifact_ref, {
        artifactRef: event.payload.artifact_ref,
        artifactType: event.payload.artifact_type,
        label: event.payload.artifact_label,
        sourceArtifactRef: event.payload.source_artifact_ref ?? undefined,
        targetArtifactRef: event.payload.target_artifact_ref ?? undefined,
        confidenceScore:
          typeof event.payload.confidence_score === "number"
            ? clamp(event.payload.confidence_score, 0, 1)
            : undefined,
        sourceBindingCount:
          typeof event.payload.counts?.source_binding_count === "number"
            ? event.payload.counts.source_binding_count
            : undefined,
        status:
          event.event_type === "candidate_validated" ? "validated" : "proposed",
        eventId: event.id,
      });
    }
  });

  const latestEvent = events[events.length - 1];
  return {
    events,
    latestEvent,
    stage: latestEvent.stage_name,
    progress: latestEvent.event_type === "run_completed" ? 100 : latestProgress,
    counts,
    proposals: Array.from(proposalsByRef.values()),
    feed: events.filter(
      (event) => event.event_type !== "run_started" || event.message.trim(),
    ),
    isRetrying: latestEvent.event_type === "stage_retrying",
    hasRecovered,
    isCompleted: latestEvent.event_type === "run_completed",
    isFailed:
      latestEvent.event_type === "run_failed" ||
      latestEvent.event_type === "run_superseded",
  };
}

export function suggestionArtifactRef(suggestion: {
  id: number;
  target_type: string;
  target_ref?: Record<string, unknown> | null;
  proposed_payload: Record<string, unknown>;
}): string {
  const targetRef = suggestion.target_ref ?? {};
  const payload = suggestion.proposed_payload;
  const artifactType =
    suggestion.target_type === "semantic_mapping"
      ? "business_meaning"
      : suggestion.target_type;
  const key = firstText(
    targetRef.entity_key,
    targetRef.meaning_key,
    targetRef.metric_key,
    targetRef.relationship_key,
    payload.name,
    payload.concept_key,
    `suggestion-${suggestion.id}`,
  );
  return `${artifactType}:${key}`;
}

export function ontologyBuildStageLabel(stage: OntologyBuildStage): string {
  return {
    inventory_normalize: "Reading source metadata",
    group: "Grouping related assets",
    infer_entities: "Inferring business entities",
    bind_sources: "Binding source evidence",
    resolve_identity: "Resolving shared identities",
    infer_relationships_metrics: "Proposing relationships and metrics",
    merge_reconcile: "Reconciling candidates",
    validate: "Validating typed output",
    persist_suggestions: "Persisting review candidates",
    completed: "Review is the next step",
  }[stage];
}

function deduplicateEvents(
  events: OntologyGenerationRunEvent[],
): OntologyGenerationRunEvent[] {
  const byId = new Map<string, OntologyGenerationRunEvent>();
  events.forEach((event) => {
    const key = String(event.event_id ?? event.id);
    if (!byId.has(key)) byId.set(key, event);
  });
  return Array.from(byId.values()).sort((left, right) => left.id - right.id);
}

function firstText(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
